import { defaultLocale, getLinguiI18n } from "@klarify/i18n";
import { Link } from "@klarify/i18n/navigation";
import type { DirectoryEntry } from "@klarify/types";
import { msg } from "@lingui/core/macro";
import { ArrowUpRight, MapPin } from "lucide-react";
import { cn } from "../../lib/utils";
import { DirectoryCardFooter } from "./directory-card-footer";
import { DirectoryHeadshot } from "./directory-headshot";
import { formatLocation } from "./format-location";
import { HEADSHOT_SHAPE_CLASS } from "./headshot-shape";

export type TherapistDirectoryEntry = DirectoryEntry;

export const TherapistDirectoryCard = (props: TherapistDirectoryEntry) => {
	const fullName = [props.firstName, props.lastName].filter(Boolean).join(" ");
	const heading = props.credentialsSuffix
		? `${fullName}, ${props.credentialsSuffix}`
		: fullName;
	const operatingLocation = formatLocation({
		country: props.operatingCountry,
		state: props.operatingState,
		city: props.operatingCity,
	});

	return (
		// No aria-label: the visible card content (name, credentials, tagline)
		// is the accessible name — an override that omits the visible text
		// fails WCAG 2.5.3 (label in name).
		<Link
			href={props.href ?? `/therapists/${props.slug}`}
			className="group flex h-full flex-col overflow-hidden rounded-2xl border border-klarify-neutral-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:border-klarify-forest-200 hover:shadow-md"
		>
			<div className="flex items-start gap-4 p-5 sm:p-6">
				<DirectoryHeadshot
					src={props.profilePicture}
					firstName={props.firstName}
					lastName={props.lastName}
					size={96}
					className={cn(
						"h-20 w-20 shrink-0 sm:h-24 sm:w-24",
						HEADSHOT_SHAPE_CLASS,
					)}
					fallbackClassName="text-2xl sm:text-3xl"
				/>
				<div className="flex min-w-0 flex-1 flex-col gap-1.5">
					<div className="flex items-start justify-between gap-2">
						{/* h2: the directory pages render these cards directly under
						    the page h1, so h3 skips a level (Lighthouse heading-order). */}
						<h2 className="font-semibold text-base text-klarify-neutral-900 leading-snug tracking-tight sm:text-lg">
							{heading}
						</h2>
						<ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-klarify-neutral-300 transition-colors group-hover:text-klarify-forest-700" />
					</div>
					{props.pronouns ? (
						<p className="text-klarify-neutral-500 text-xs">{props.pronouns}</p>
					) : null}
					{operatingLocation ? (
						<p className="inline-flex items-center gap-1.5 text-klarify-neutral-600 text-sm">
							<MapPin className="h-3.5 w-3.5 text-klarify-neutral-400" />
							<span className="truncate">{operatingLocation}</span>
						</p>
					) : null}
				</div>
			</div>
			{props.tagline ? (
				<p className="line-clamp-3 px-5 pb-4 text-[14px] text-klarify-neutral-700 leading-relaxed sm:px-6">
					{props.tagline}
				</p>
			) : null}
			<DirectoryCardFooter
				specialties={props.specialties}
				sessionFormats={props.sessionFormats}
				moreLabel={getLinguiI18n(defaultLocale)._(msg`more`)}
			/>
		</Link>
	);
};

TherapistDirectoryCard.displayName = "TherapistDirectoryCard";
